import type { UpdatesConfig } from '@urql/exchange-graphcache';

import type {
  AddLineItemToCartMutation,
  AddSubscriptionPaymentMethodMutation,
  ApplyRewardMutation,
  DeleteAddressMutation,
  DeleteBillingAccountInput,
  DeleteBillingAccountMutation,
  EditLineItemInCartMutation,
  LoginResponseUnion,
  OptInUserMutation,
  RemoveFromCartMutation,
  RemoveRewardMutation,
  ReorderLineItemMutation,
  ReorderOrderMutation,
  SubmitBraintreeBillingAccountMutation,
  SubmitOrderMutation,
  SubscribeMutation,
} from '@order/graphql';

import type { AddAddressMutation } from '../../../../components/Address/GraphQL/Address.generated';
import {
  deleteBillingAccountCacheUpdate,
  invalidateCartCacheEntry,
  submitBraintreeBillingAccountCacheUpdate,
  updateCartCache,
} from './utils';
import {
  addAddressCacheUpdate,
  deleteAddressCacheUpdate,
} from './utils/addresses';
import {
  removeSubscriptionPaymentMethodCacheUpdate,
  updatePaymentSubscriptionProfile,
  updateSubscriptionPaymentMethodCacheUpdate,
} from './utils/sweetpass';

/**
 * Graphcache | Updates
 * A nested mapping of updater functions for mutation (and subscription) fields,
 * which may be used to add side-effects that update other parts of the cache
 * when the given subscription or mutation field is written to the cache.
 *
 * @see {@link: https://formidable.com/open-source/urql/docs/graphcache/cache-updates/}
 */

export const updates: UpdatesConfig = {
  Mutation: {
    // this force invalidates all the cache entries on logout
    logout(_result, _args, cache, _info) {
      cache.invalidate('Query');
      cache.invalidate('Mutation');
      cache.invalidate('Subscription');
    },

    // logging in should invalidate the loyalty profile query.
    login(result: LoginResponseUnion, _args, cache, _info) {
      if (result.__typename !== 'LoginSuccess') return;
      cache.invalidate('Query', 'loyaltyProfile');
    },

    // Invalidating loyaltyProfile query when new subscription created
    // This will force app to re-fetch loyaltyProfile where we'll have upgraded tier
    // ticket: https://sweetgreen.atlassian.net/browse/CELS-671
    createSubscription(result: SubscribeMutation, _args, cache) {
      if (result.createSubscription?.__typename !== 'CreateSubscriptionResult')
        return;
      cache.invalidate('Query', 'loyaltyProfile');
    },

    // Invalidating loyaltyProfile query when new opt-in to sweetpass,
    // This will force app to re-fetch loyaltyProfile where we'll have upgraded tier.
    optInFreeTier(result: OptInUserMutation, _args, cache) {
      if (!result.optInFreeTier) return;
      cache.invalidate('Query', 'loyaltyProfile');
    },

    //
    // ─── CART UPDATES ────────────────────────────────────────────────
    //

    /*
      The following helpers modifies the 'cart' cache entry for the respective mutations.

      urql uses the `__typename` and `id` fields to update the cache entries,
      but sometimes we need to override the cart entry when the ID is different;
      therefore, we must manually handle these updates.

      Add here any cart-related update handler.
     */

    addLineItemToCart(result: AddLineItemToCartMutation, _args, cache) {
      updateCartCache(cache, result.addLineItemToCart);
    },
    editLineItemInCart(result: EditLineItemInCartMutation, _args, cache) {
      updateCartCache(cache, result.editLineItemInCart);
    },
    removeFromCart(result: RemoveFromCartMutation, _args, cache) {
      updateCartCache(cache, result.removeFromCart);
    },
    applyReward(result: ApplyRewardMutation, _args, cache) {
      updateCartCache(cache, result.applyReward);
    },
    removeReward(result: RemoveRewardMutation, _args, cache) {
      updateCartCache(cache, result.removeReward);
    },
    reorderOrder(result: ReorderOrderMutation, _args, cache) {
      updateCartCache(cache, result.reorderOrder);
    },
    reorderLineItem(result: ReorderLineItemMutation, _args, cache) {
      updateCartCache(cache, result.reorderLineItem);
    },
    addSubscriptionPaymentMethod(
      result: AddSubscriptionPaymentMethodMutation,
      _args,
      cache,
    ) {
      const isFailed =
        result.addSubscriptionPaymentMethod.__typename !==
        'AddSubscriptionPaymentMethodSuccessResponse';

      if (isFailed) {
        return;
      }

      const { creditCard } = result.addSubscriptionPaymentMethod;

      updatePaymentSubscriptionProfile(cache, creditCard);
    },
    submitOrderUsingBillingAccount(result: SubmitOrderMutation, _args, cache) {
      const isSubmitted =
        result?.submitOrderUsingBillingAccount?.__typename ===
        'SubmitOrderUsingBillingAccountSuccess';

      if (isSubmitted) invalidateCartCacheEntry(cache);
    },

    // ─── Billing Accounts Cache ──────────────────────────────────────

    submitBraintreeBillingAccount(
      result: SubmitBraintreeBillingAccountMutation,
      _args,
      cache,
    ) {
      submitBraintreeBillingAccountCacheUpdate(cache, result);
    },
    deleteBillingAccount(result: DeleteBillingAccountMutation, args, cache) {
      deleteBillingAccountCacheUpdate(
        cache,
        result,
        args.input as DeleteBillingAccountInput,
      );
    },

    // ─── Addresses Cache ─────────────────────────────────────────────

    addAddress(result: AddAddressMutation, _args, cache) {
      addAddressCacheUpdate(cache, result);
      const isAdded = result?.addAddress?.__typename === 'AddAddressSuccess';

      if (isAdded) invalidateCartCacheEntry(cache);
    },
    deleteAddress(result: DeleteAddressMutation, args, cache) {
      deleteAddressCacheUpdate(
        cache,
        result,
        args as { readonly addressId: string },
      );
    },

    // ─── Subscription Payment Methods Cache ─────────────────────────────────────────────

    updateSubscriptionPaymentMethod: updateSubscriptionPaymentMethodCacheUpdate,
    removeSubscriptionPaymentMethod: removeSubscriptionPaymentMethodCacheUpdate,
  },
  Subscription: {},
};
